import React, { FunctionComponent, useEffect, useState } from 'react';
import CategoriesConnection from '../../../../utils/connections/categories';
import { IGetCategories, ICategories } from '../../../../utils/models';
import { handleClearFilters, loadFilters } from '../../../../utils/filterHelper';
import { deepClone, handleToast } from '../../../../utils/helpers';
import { getCategoriesState } from '../../../../utils/states';
import Spinner from '../../../../global/atoms/Spinner/Spinner';
import HeaderButtons from '../../../../global/atoms/headerButtons/HeaderButtons';
import ButtonFilters from '../../../../global/atoms/ButtonFilters';
import ButtonAddNew from '../../../../global/atoms/ButtonAddNew';
import {
  DeleteSelectedButton,
  DeleteSingleButton,
  ToggleAllCheckbox,
  ToggleSingleCheckbox,
} from '../../../../utils/toggleToDeleteHelper';
import WhiteCard from '../../../../global/atoms/WhiteCard';
import List from '../../../../global/table/List';
import { Cell, Column, HeaderCell } from 'rsuite-table';
import arrow from '../../../../assets/svg/arrow_down_small.svg';
import ButtonListRedirect from '../../../../global/atoms/ButtonListRedirect';
import ActionsContainer from '../../../../global/atoms/ActionsContainer';
import settings from '../../../../assets/svg/settings.svg';
import Pagination from '../../../../global/pagination/Pagination';
import Filters from '../../../../global/filters/Filters';
import Input from '../../../../global/atoms/Input';
import Select from '../../../../global/atoms/Select';
import { useHistory } from 'react-router-dom';
import FiltersStorage from '../../../../utils/FiltersStorage';
import ProductsConnection from '../../../../utils/connections/products';

interface ICategoriesList {}

const CategoriesList: FunctionComponent<ICategoriesList> = () => {
  const defaultFilters = deepClone(getCategoriesState);
  const [data, setData] = useState<ICategories | null>(null);
  const [filters, setFilters] = useState<IGetCategories>(loadFilters(defaultFilters));
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState<any | 'loading'>('loading');
  const [chosenToDelete, setChosenToDelete] = useState<Array<string>>([]);
  const history = useHistory();
  const [toggleFilters, setToggleFilters] = useState<boolean>(false);

  useEffect(() => {
    ProductsConnection.getFiltersProducts().then((data) => {
      setLoading(false);
      setForm(data.data);
    });
  }, []);

  const reloadData = () => {
    CategoriesConnection.getCategories(filters).then((data) => {
      setLoading(false);
      setData(data.data);
    });
  };

  useEffect(() => {
    setFilters((state: IGetCategories) => ({
      ...state,
      requestPaginate: {
        page: 1,
        limit: state.requestPaginate.limit,
      },
    }));
    reloadData();
  }, [filters.requestPaginate.limit]);

  useEffect(() => {
    reloadData();
  }, [filters.requestPaginate.page, filters.requestOrder]);

  const handleToggleSort = (event: any) => {
      if (filters.requestOrder.field === event.currentTarget.dataset.field) {
        setFilters({
          ...filters,
          requestOrder: {
            field: filters.requestOrder.field,
            order: filters.requestOrder.order === 'asc' ? 'desc' : 'asc',
          },
        });
      } else {
        setFilters({
          ...filters,
          requestOrder: {
            field: event.currentTarget.dataset.field,
            order: 'asc',
          },
        });
      }
    },
    handleToggleFilters = () => {
      setToggleFilters((state) => !state);
    },
    handleSubmitFilters = (event: any) => {
      event.preventDefault();

      const filtersStorage = new FiltersStorage();
      filtersStorage.save(filters);

      CategoriesConnection.getCategories({
        ...filters,
        requestPaginate: {
          page: 1,
          limit: filters.requestPaginate.limit,
        },
      }).then((data) => {
        setLoading(false);
        setToggleFilters(false);
        setData(data.data);
        setFilters((state: IGetCategories) => ({
          ...state,
          requestPaginate: { page: 1, limit: state.requestPaginate.limit },
        }));
      });
    },
    handleEdit = (event: any) => {
      history.push(`/products/category/${event.currentTarget.dataset.id}/edit`);
    };

  if (loading) return <Spinner />;
  if (data === null || form === 'loading') return null;

  const handleDeleteSelected = (deleteId: string) => {
    let toDelete = chosenToDelete.map((id) => ({ id: id }));
    if (deleteId !== undefined) {
      toDelete = [{ id: deleteId }];
    }
    handleToast(CategoriesConnection.batchDelete(toDelete)).then(() => {
      setChosenToDelete([]);
      reloadData();
    });
  };

  return (
    <>
      <HeaderButtons>
        <ButtonFilters onClick={handleToggleFilters} />
        <ButtonAddNew form={form} />
        <DeleteSelectedButton chosenToDelete={chosenToDelete} callback={handleDeleteSelected} form={form} />
      </HeaderButtons>
      <WhiteCard mobileTransparent={true} padding={false}>
        <List data={data.data} count={data.data.length}>
          <Column width={40} align="left">
            <HeaderCell depth={0} width={0}>
              <ToggleAllCheckbox data={data.data} state={chosenToDelete} setState={setChosenToDelete} />
            </HeaderCell>
            <Cell width={100} depth={0}>
              {(rowData) => (
                <ToggleSingleCheckbox state={chosenToDelete} setState={setChosenToDelete} selectedId={rowData.id} />
              )}
            </Cell>
          </Column>
          <Column flexGrow={1} minWidth={300} align="left">
            <HeaderCell depth={0} width={0}>
              <span
                style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                onClick={handleToggleSort}
                data-field={'name'}
              >
                Nazwa
                {filters.requestOrder.field === 'name' &&
                  (filters.requestOrder.order === 'asc' ? (
                    <img src={arrow} alt={'sort'} />
                  ) : (
                    <img src={arrow} alt={'sort'} style={{ transform: 'rotate(180deg)' }} />
                  ))}
              </span>
            </HeaderCell>
            <Cell width={0} depth={0}>
              {(rowData) => (
                <ButtonListRedirect redirectUrl={`/products/category/${rowData.id}/edit`}>
                  {rowData.name}
                </ButtonListRedirect>
              )}
            </Cell>
          </Column>

          <Column verticalAlign={'middle'} align="right" fixed={'right'}>
            <HeaderCell depth={0} width={0}>
              Akcje
            </HeaderCell>
            <Cell depth={0} width={0}>
              {(rowData) => (
                <ActionsContainer>
                  <img
                    src={settings}
                    style={{ cursor: 'pointer' }}
                    alt={'Idź do'}
                    data-id={rowData.id}
                    onClick={handleEdit}
                  />
                  <DeleteSingleButton callback={handleDeleteSelected.bind(null, rowData.id)} form={form} />
                </ActionsContainer>
              )}
            </Cell>
          </Column>
        </List>
      </WhiteCard>
      <Pagination
        count={data.count}
        page={filters.requestPaginate.page.toString()}
        limit={filters.requestPaginate.limit.toString()}
        setState={setFilters}
      />
      <Filters
        toggleFilters={toggleFilters}
        handleToggleFilters={handleToggleFilters}
        handleSubmit={handleSubmitFilters}
        handleClear={handleClearFilters.bind(null, setFilters, defaultFilters, reloadData)}
      >
        <Input
          value={filters.name}
          placeholder={'Nazwa'}
          name={'name'}
          type={'text'}
          setState={setFilters}
          state={filters}
        />
        <Select
          placeholder={'Klient'}
          name={'customerId'}
          value={filters.customerId}
          options={form.customers.options}
          disabled={false}
          state={filters}
          setState={setFilters}
        />
      </Filters>
    </>
  );
};

export default CategoriesList;
